import { useEffect, useState } from "react"

/**
 * A React hook that listens to and returns the state of a CSS media query.
 *
 * @param query - A valid CSS media query string (e.g., "(min-width: 768px)" or "(prefers-color-scheme: dark)")
 * @returns True if the media query matches the current state, false otherwise
 *
 * @example
 * ```tsx
 * function DarkModeAwareComponent() {
 *   const isDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
 *
 *   return (
 *     <div>
 *       Current theme: {isDarkMode ? 'Dark' : 'Light'}
 *     </div>
 *   );
 * }
 */
export function useMediaQuery(query: string): boolean {
  const [value, setValue] = useState(() => typeof window !== "undefined" ? matchMedia(query).matches : false)

  useEffect(() => {
    function onChange(event: MediaQueryListEvent) {
      setValue(event.matches)
    }

    const result = matchMedia(query)
    result.addEventListener("change", onChange)
    setValue(result.matches)

    return () => result.removeEventListener("change", onChange)
  }, [query])

  return value
}
